import React, { useEffect, useState } from "react";
import OpenModal from "../OpenModel";
import EditorModal from "../user/EditorModal";
import translations from "../../translation"; // Import translations

const RejectedTable = ({ language }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
  const [content, setContent] = useState("");
  const [contentId, setContentId] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const translate = (key) => translations[language][key] || key; // Translation function

  useEffect(() => {
    fetchRejectedActivities();
  }, []);

  useEffect(() => {
    const filtered = activities.filter((activity) =>
      activity.title.toLowerCase().includes(filterText.toLowerCase())
    );
    setFilteredActivities(filtered);
    setCurrentPage(1);
  }, [filterText, activities]);

  const fetchRejectedActivities = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const formData = new FormData();
      formData.append("sender_id", user.id);

      const response = await fetch(
        `${process.env.REACT_APP_CHECK_USER_API}/api/user/rejected`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      setActivities(data.data || []);
      setFilteredActivities(data.data || []);
    } catch (error) {
      console.error("Error fetching rejected activities:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewClick = (rowData) => {
    setModalData(rowData);
    setIsModalOpen(true);
  };

  const handleUpdateClick = (rowData) => {
    setContent(rowData.content);
    setContentId(rowData.id);
    setSelectedRow(rowData);
    setIsEditorModalOpen(true);
  };

  const totalPages =
    filteredActivities.length > 0
      ? Math.ceil(filteredActivities.length / itemsPerPage)
      : 0;

  const paginatedActivities = filteredActivities.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
  };

  return (
    <>
      <div className="bg-white shadow rounded-lg mb-8 w-full">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <h3 className="text-xlg leading-6 font-medium text-gray-900">
            {translate("rejectedActivities")}
          </h3>
          <div className="flex items-center space-x-3">
            <div className="relative shadow-md">
              <input
                type="text"
                value={filterText}
                onChange={handleFilterChange}
                className="form-input pl-10 pr-4 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder={translate("searchByTitle")}
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="px-4 py-6 text-center text-gray-500">{translate("loading")}</div>
        ) : (
          <div className="border-t border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {translate("title")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {translate("rejectedBy")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {translate("rejectedDate")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {translate("status")}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {translate("actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedActivities && paginatedActivities.length > 0 ? (
                    paginatedActivities.map((row, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {row.title}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {row.rejected_by_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(row.rejected_date).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            {translate(row.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleViewClick(row)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-custom hover:text-custom-dark border border-custom rounded-button"
                          >
                            {translate("view")}
                          </button>
                          <button
                            onClick={() => handleUpdateClick(row)}
                            className="ml-2 inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-500 hover:text-blue-700 border border-blue-500 rounded-button"
                          >
                            {translate("update")}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500"
                      >
                        {translate("noRejectedActivities")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center px-4 py-3 bg-white border-t border-gray-200">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm font-medium text-gray-500 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                {translate("previous")}
              </button>
              <p className="text-sm text-gray-500">
                {translate("page")} {currentPage} {translate("of")} {totalPages}
              </p>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-500 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                {translate("next")}
              </button>
            </div>
          </div>
        )}
      </div>

      <OpenModal
        isOpen={isModalOpen}
        language={language}
        onClose={() => setIsModalOpen(false)}
        title={translate("rejectionDetails")}
        tableHeaders={[translate("field"), translate("value")]}
        tableData={
          modalData
            ? Object.entries(modalData).map(([field, value]) => ({
                field,
                value,
              }))
            : []
        }
      />

      <EditorModal
        isOpen={isEditorModalOpen}
        onClose={() => setIsEditorModalOpen(false)}
        content={content}
        setContent={setContent}
        attachment={attachment}
        setAttachment={setAttachment}
        id={contentId}
        language={language}
      />
    </>
  );
};

export default RejectedTable;
